<template>
  <div class="my-4 mx-6">
    <div class="mb-6">
      <router-link class="breadcrumb-link" to="/publisher/reports">
        Reports
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-link">Plugin Installs</span>
    </div>
    <div
      class="PluginInstallsReport__header d-flex flex-column flex-sm-row justify-space-between align-sm-center gap-3 pa-0 mt-4"
    >
      <div class="d-flex flex-column flex-sm-row align-sm-center gap-3">
        <v-select
          v-model="filter.sortBy"
          :items="sortOptions"
          item-text="label"
          item-value="value"
          class="PluginInstallsReport__select-filter flex-grow-0"
          height="40"
          dense
          dark
          hide-details
          :readonly="reportReady"
          @click="displayReadonlyAlert"
        />
        <v-select
          v-model="filter.period"
          :items="periods"
          item-text="label"
          item-value="value"
          class="PluginInstallsReport__select-filter flex-grow-0"
          height="40"
          dense
          dark
          hide-details
          :readonly="reportReady"
          @click="displayReadonlyAlert"
        />
        <RangePicker
          v-if="filter.period == 'custom'"
          :start-date.sync="filter.startDate"
          :end-date.sync="filter.endDate"
          :read-only="reportReady"
          :click="displayReadonlyAlert"
        />
        <v-checkbox
          dark
          v-if="filter.period == 'all'"
          v-model="filter.readyForSaleOnly"
          @click="displayReadonlyAlert"
          label="Ready for Sale Only"
          class="mr-4"
          :readonly="reportReady"
          dense
          hide-details
        ></v-checkbox>
        <v-checkbox
          dark
          v-if="filter.period == 'all'"
          v-model="filter.hideZeroInstalls"
          @click="displayReadonlyAlert"
          label="Hide Zero Installs"
          :readonly="reportReady"
          dense
          hide-details
        ></v-checkbox>
      </div>
      <div class="d-flex" v-if="reportReady">
        <v-btn
          class="v-btn primary-btn mr-4"
          @click="onDowloadReport"
          elevation="2"
          large
        >
          <v-icon dark left> mdi-download </v-icon>
          Download
        </v-btn>
        <v-btn
          class="v-btn reset-btn"
          @click="onResetFilter"
          elevation="2"
          large
        >
          <v-icon dark left> mdi-refresh </v-icon>
          Reset
        </v-btn>
      </div>
      <v-btn
        class="v-btn primary-btn"
        @click="onRunReport"
        elevation="2"
        large
        v-else
      >
        <v-icon dark left> mdi-play </v-icon>
        Run
      </v-btn>
    </div>
    <div class="PluginInstallsReport__body">
      <v-row v-if="reportReady" class="mt-4">
        <v-col>
          <v-data-table
            :headers="headers"
            :items="tableData"
            ref="dataTable"
            class="data-table"
            @click:row="handleClickPluginRow"
          >
            <template v-slot:item.installs_count="{ item }">
              {{ item.installs_count.toLocaleString() }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                small
                dark
              >
                {{ item.status }}
              </v-chip>
            </template>
            <template v-slot:item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <div v-if="loading" class="spinner">
        <FlamePreloader :compact="true" :size="100" />
      </div>
    </div>
  </div>
</template>

<script>
import FlamePreloader from '@/components/_Common/FlamePreloader'
import RangePicker from './RangePicker.vue'
import jsPDF from 'jspdf'
import { getFirstMondayAndLastSunday } from '@/utils/common'
import marketplaceAPI from '@/services/marketplace'
import moment from 'moment'
import 'jspdf-autotable'

export default {
  name: 'PluginInstallsReport',
  components: {
    RangePicker,
    FlamePreloader,
  },
  data() {
    const periods = [
      { value: 'all', label: 'All Time' },
      { value: 'custom', label: 'Custom Range' },
    ]

    const [firstMonday, lastSunday] = getFirstMondayAndLastSunday()

    return {
      periods,
      sortOptions: [
        { value: 'installs_count', label: 'Sort By Installs' },
        { value: 'name', label: 'Sort By Name' },
        { value: 'status', label: 'Sort By Status' },
        { value: 'created_at', label: 'Sort By Created Date' },
        { value: 'developer_name', label: 'Sort By Developer' },
      ],
      filter: {
        period: 'all',
        sortBy: 'installs_count',
        readyForSaleOnly: false,
        hideZeroInstalls: true,
        startDate: firstMonday,
        endDate: lastSunday,
      },
      data: [],
      loading: false,
      reportReady: false,
      tableData: [],
      reportGenerationId: null,
    }
  },
  computed: {
    headers() {
      return [
        {
          text: 'Name',
          align: 'start',
          sortable: false,
          value: 'name',
        },
        { text: 'Slug', value: 'slug' },
        { text: 'Developer', value: 'developer_name' },
        {
          text: 'Installs',
          value: 'installs_count',
          align: 'end',
          sortable: false,
        },
        {
          text: 'Status',
          value: 'status',
          align: 'center',
          sortable: false,
          width: '120px',
        },
        {
          text: 'Created',
          value: 'created_at',
          align: 'start',
          sortable: false,
          width: '150px',
        },
      ]
    },
  },
  methods: {
    /**
     * Convert date to ISO 8601 format
     * @param {Date|string} date - Date to convert
     * @returns {string} ISO 8601 formatted date string
     */
    formatDateForAPI(date) {
      if (!date) return null
      const d = new Date(date)
      return d.toISOString()
    },

    /**
     * Format date for display
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date string
     */
    formatDate(dateString) {
      if (!dateString) return ''
      return moment(dateString).format('MMM DD, YYYY')
    },

    /**
     * Get status color for chip
     * @param {string} status - Plugin status
     * @returns {string} Color name
     */
    getStatusColor(status) {
      const colors = {
        active: 'success',
        draft: 'warning',
        suspended: 'error',
        submitted: 'info',
        approved: 'info',
        published: 'success',
      }
      return colors[status] || 'grey'
    },

    // Main Action part of the component.
    // Run button click event handler
    async onRunReport() {
      this.loading = true

      try {
        // Build filters
        const filters = {
          plugin_status: 'all',
          ready_for_sale_only: this.filter.readyForSaleOnly,
          hide_zero_installs: this.filter.hideZeroInstalls,
          categories: [],
          tags: [],
          developer_ids: [],
        }

        // Set time range
        if (this.filter.period === 'custom') {
          if (!this.filter.startDate || !this.filter.endDate) {
            alert('Please select both start and end date.')
            this.loading = false
            return
          }
          filters.time_range = 'custom'
          filters.start_date = this.formatDateForAPI(this.filter.startDate)
          filters.end_date = this.formatDateForAPI(this.filter.endDate)
        } else {
          filters.time_range = 'all_time'
        }

        // Build sort options
        const sort = {
          field: this.filter.sortBy,
          direction: 'desc',
        }

        // Build pagination
        const pagination = {
          page: 1,
          per_page: 100, // Get more results for plugin installs
        }

        // Generate report
        const result = await marketplaceAPI.generatePluginInstallsReport({
          filters,
          sort,
          pagination,
          generate_pdf: false,
        })

        // Store generation ID for potential PDF download
        if (result && result.report_generation_id) {
          this.reportGenerationId = result.report_generation_id
        }

        // Transform API response to match existing component structure
        if (result && result.data && result.data.plugins) {
          // Store full data with all fields from API
          this.data = result.data.plugins.map((plugin) => ({
            plugin_id: plugin.plugin_id,
            id: plugin.plugin_id,
            slug: plugin.slug,
            name: plugin.name,
            developer_name: plugin.developer_name,
            developer_id: plugin.developer_id,
            installs_count: plugin.installs_count || 0,
            status: plugin.status,
            created_at: plugin.created_at,
          }))

          // Map to table data format for display
          this.tableData = this.data.map((plugin) => ({
            id: plugin.plugin_id,
            plugin_id: plugin.plugin_id,
            slug: plugin.slug,
            name: plugin.name,
            developer_name: plugin.developer_name,
            developer_id: plugin.developer_id,
            installs_count: plugin.installs_count,
            status: plugin.status,
            created_at: plugin.created_at,
          }))

          this.reportReady = true
        } else {
          console.error('Unexpected API response format:', result)
          alert('Failed to generate report. Please try again.')
        }
      } catch (error) {
        console.error('Error generating report:', error)
        alert(`Failed to generate report: ${error.message || 'Unknown error'}`)
      } finally {
        this.loading = false
      }
    },

    // Download button click event handler
    async onDowloadReport() {
      const source = this.$refs['dataTable']

      // Build column headers with all relevant fields
      const columnHeader = [
        'Name',
        'Slug',
        'Developer',
        'Installs',
        'Status',
        'Created',
      ]

      // Build rows with all data
      const rows = source.items.map((element) => [
        element.name || '',
        element.slug || '',
        element.developer_name || '',
        element.installs_count || 0,
        element.status || '',
        element.created_at ? this.formatDate(element.created_at) : '',
      ])

      const pdfName = 'Plugin Installs Report'
      const doc = new jsPDF()
      doc.autoTable(columnHeader, rows, {
        startY: 10,
        headStyles: { fillColor: [66, 139, 202] },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 50 }, // Name
          1: { cellWidth: 40 }, // Slug
          2: { cellWidth: 40 }, // Developer
          3: { cellWidth: 25, halign: 'right' }, // Installs
          4: { cellWidth: 30, halign: 'center' }, // Status
          5: { cellWidth: 35 }, // Created
        },
      })
      doc.save(pdfName + '.pdf')
    },

    // Reset Button Click event handler
    onResetFilter() {
      this.reportReady = false
      this.loading = false
      this.reportGenerationId = null
    },

    displayReadonlyAlert() {
      if (this.reportReady)
        alert(
          'Filters are readonly and please click on Reset button to restart filter.'
        )
    },

    handleClickPluginRow(plugin) {
      this.$router.push({
        name: 'plugin_detail',
        params: { pluginId: plugin.slug },
      })
    },
  },
}
</script>
<style lang="scss">
$style: PluginInstallsReport;
.#{$style} {
  padding: 42px 24px 24px;
  &__row {
    margin-bottom: 58px;
  }

  &__header {
    .v-btn.primary-btn {
      background-color: $B1;
      color: white;
    }
    .v-btn.reset-btn {
      background-color: $R1;
      color: white;
    }

    .date-picker {
      border-radius: 8px;
      padding: 8px 10px;
      background-color: hsla(0, 0%, 100%, 0.08);
      border: 1px solid #5e636d;
      margin-right: 10px;
    }
  }

  &__select-filter {
    border: 1px solid #5e636d;
    border-radius: 8px;
    padding: 0 10px;
    background-color: hsla(0, 0%, 100%, 0.08);
    @media screen and (min-width: 600px) {
      width: 200px;
    }
  }

  &__body {
    min-height: 500px;
    position: relative;
    .spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      margin-top: -50px;
      margin-left: -50px;
      width: 100px;
      height: 100px;
    }
  }
}
</style>
